import { useSeo } from "../lib/useSeo";

export const COMPANY = {
  name: "Voliciti Group Ventures Ltd",
  phone: "0203 051 4338",
  email: "contact@volicitigroupventuresul.com",
  site: "Universal Short-Command Prompt Dictionary™ (AI Shorthand)",
  updated: "8 September 2026",
};

const TRUST = "AI Shorthand commands are simple prompting shortcuts designed to help users tell AI what they want it to do. They are not official built-in slash commands supplied by ChatGPT, Claude or Gemini, and we are not affiliated with, or endorsed by, OpenAI, Anthropic or Google.";

export const LEGAL = {
  privacy: {
    title: "Privacy Policy",
    description: "How Voliciti Group Ventures Ltd collects, uses and protects your personal data, and your rights under UK GDPR.",
    sections: [
      { h: "1. Who we are", body: [
        `${COMPANY.site} is operated by ${COMPANY.name} ("we", "us", "our"). We are the data controller responsible for your personal data.`,
        { ul: [
          `Company: ${COMPANY.name}`,
          `Telephone: ${COMPANY.phone}`,
          `Email: ${COMPANY.email}`,
        ]},
        "This policy explains what personal data we collect, why we collect it, how long we keep it, who we share it with, and your rights under the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.",
      ]},
      { h: "2. Personal data we collect", body: [
        { ul: [
          "Account data — your name, email address and a securely hashed password when you create an account.",
          "Membership and billing data — your membership tier, purchase history, voucher/discount codes used, renewal dates and transaction references. Card payments are processed by Stripe; we never see or store your full card details.",
          "Usage data — searches you run, commands you view, copy or favourite, and features you use. We use this to improve the command library and your experience.",
          "Communications data — messages you send us and emails we send you (e.g. purchase confirmations, renewal reminders, password resets, product updates).",
          "Technical data — IP address, browser type and device information collected automatically for security and to keep the service working.",
        ]},
      ]},
      { h: "3. Why we use your data (lawful bases)", body: [
        { ul: [
          "To provide your membership and account — performance of our contract with you.",
          "To take payment, prevent fraud and keep records — performance of contract and legal obligation.",
          "To improve the dictionary, understand which commands are most useful and fix problems — our legitimate interests in running and improving the service.",
          "To send you service emails (receipts, renewal reminders, password resets) — performance of contract.",
          "To send you product updates about new commands and features — our legitimate interests; you can opt out at any time via the link in any such email or by contacting us.",
          "To keep the service secure — our legitimate interests and legal obligation.",
        ]},
      ]},
      { h: "4. Cookies", body: [
        "We use essential cookies only (to keep you securely logged in) and small items of local storage for your preferences. We do not use advertising or cross-site tracking cookies. See our Cookie Policy for full details and your choices.",
      ]},
      { h: "5. Who we share your data with", body: [
        "We never sell your personal data. We share it only with trusted service providers (processors) who help us run the service, under contracts that protect your data:",
        { ul: [
          "Stripe — secure payment processing and subscription billing.",
          "Our hosting and database providers — to store and serve your account and the command library.",
          "Our email delivery provider — to send service and product emails.",
        ]},
        "We may also disclose data where required by law, to enforce our terms, or to protect the rights, property or safety of our users.",
      ]},
      { h: "6. International transfers", body: [
        "Some of our providers may process data outside the UK. Where they do, we ensure appropriate safeguards are in place, such as UK adequacy regulations, the UK International Data Transfer Agreement, or the UK Addendum to the EU Standard Contractual Clauses.",
      ]},
      { h: "7. How long we keep your data", body: [
        { ul: [
          "Account and membership data — for as long as you hold an account, and up to 6 years afterwards where needed for legal, tax and accounting purposes.",
          "Usage data — in identifiable form for up to 24 months, after which it is aggregated or deleted.",
          "Communications — for up to 24 months after your last contact with us.",
        ]},
      ]},
      { h: "8. Your rights", body: [
        "Under UK GDPR you have the right to:",
        { ul: [
          "Access — request a copy of the personal data we hold about you.",
          "Rectification — ask us to correct inaccurate or incomplete data.",
          "Erasure — ask us to delete your data (\"right to be forgotten\").",
          "Restriction — ask us to limit how we use your data.",
          "Portability — receive your data in a structured, machine-readable format.",
          "Object — object to processing based on our legitimate interests, and to direct marketing at any time.",
          "Withdraw consent — where processing is based on consent, withdraw it at any time.",
        ]},
        `To exercise any right, email ${COMPANY.email} or call ${COMPANY.phone}. We will respond within one month. We may need to verify your identity first.`,
      ]},
      { h: "9. Complaints", body: [
        "If you are unhappy with how we handle your data, please contact us first so we can try to resolve it. You also have the right to complain to the UK Information Commissioner's Office (ICO) at ico.org.uk or on 0303 123 1113.",
      ]},
      { h: "10. Children", body: [
        "The service is intended for users aged 16 and over. We do not knowingly collect personal data from children under 16. If you believe a child has provided us with personal data, please contact us and we will delete it.",
      ]},
      { h: "11. Changes to this policy", body: [
        "We may update this policy from time to time. Material changes will be highlighted on this page and, where appropriate, notified to you by email. Continued use of the service after changes take effect constitutes acceptance of the updated policy.",
      ]},
    ],
  },

  terms: {
    title: "Terms of Service",
    description: "The terms that govern your use of the Universal Short-Command Prompt Dictionary™, operated by Voliciti Group Ventures Ltd.",
    sections: [
      { h: "1. About these terms", body: [
        `These Terms of Service ("Terms") govern your use of ${COMPANY.site}, operated by ${COMPANY.name} (company contact: ${COMPANY.email}, ${COMPANY.phone}). By creating an account or using the service you agree to these Terms, our Subscription Terms, Privacy Policy, Cookie Policy and AI Disclaimer.`,
      ]},
      { h: "2. The service", body: [
        "We provide a searchable dictionary of short-command prompt shortcuts, examples and related tools for use with third-party AI services such as ChatGPT, Claude and Gemini.",
        TRUST,
      ]},
      { h: "3. Your account", body: [
        { ul: [
          "You must provide accurate information and keep your login details confidential.",
          "Your membership is personal and non-transferable. You must not share your account or allow others to access the dictionary through it.",
          "You must be at least 16 years old to create an account.",
          "You are responsible for all activity under your account.",
        ]},
      ]},
      { h: "4. Acceptable use", body: [
        "You agree not to:",
        { ul: [
          "Copy, scrape, resell, publish or redistribute the command library or any substantial part of it.",
          "Use the service for unlawful purposes or to create unlawful, harmful or misleading content.",
          "Attempt to gain unauthorised access to the service, other accounts or our systems.",
          "Use automated tools to extract data from the service.",
        ]},
        "We may suspend or terminate accounts that breach these Terms.",
      ]},
      { h: "5. Intellectual property", body: [
        `The command library, site content, branding and design are owned by ${COMPANY.name} and protected by copyright and other intellectual property laws. Your membership grants you a personal, non-exclusive, non-transferable licence to use the commands for your own personal or business tasks. Content you create using AI tools with our commands belongs to you, subject to the terms of the relevant AI provider.`,
      ]},
      { h: "6. Third-party AI services", body: [
        "ChatGPT, Claude, Gemini and other AI tools are separate services with their own terms and pricing. We are not responsible for their availability, their outputs, or how they process your data. Quick-launch buttons and copy features simply help you take your prompt to those services.",
      ]},
      { h: "7. Availability and changes", body: [
        "We aim to keep the service available at all times but do not guarantee uninterrupted access. We may update, add to or modify the command library and features to improve the service. If we make a change that materially reduces the core service you have paid for, you may contact us about a pro-rata remedy.",
      ]},
      { h: "8. Liability", body: [
        "Nothing in these Terms excludes or limits liability for death or personal injury caused by negligence, fraud, or any other liability that cannot be excluded by law. Subject to that, we are not liable for: loss of profit, business or goodwill; content produced by third-party AI services; or losses that were not reasonably foreseeable. Our total liability to you in any 12-month period is limited to the amount you paid us in that period.",
      ]},
      { h: "9. Consumer cancellation rights", body: [
        "Membership gives you immediate access to digital content. When you sign up you expressly request immediate performance and acknowledge that, once your 24-hour free trial ends and payment is taken (or, for Lifetime, once access is granted), you lose the statutory 14-day cooling-off right and payments are non-refundable. Your cancellation window is the 24-hour free trial — cancel within it and you pay nothing. See the Subscription Terms for full details. Nothing in these Terms affects your statutory rights where the service is faulty or not as described.",
      ]},
      { h: "10. Termination", body: [
        "You may close your account at any time from My Account or by contacting us. We may suspend or terminate your access if you materially breach these Terms. Clauses which by their nature should survive termination (including intellectual property and liability) will survive.",
      ]},
      { h: "11. General", body: [
        { ul: [
          "These Terms are governed by the laws of England and Wales, and the courts of England and Wales have exclusive jurisdiction.",
          "If any provision is found unenforceable, the remainder continues in effect.",
          "We may update these Terms; material changes will be notified on the site or by email.",
        ]},
      ]},
    ],
  },

  "subscription-terms": {
    title: "Subscription Terms",
    description: "Membership plans, free trial, renewal, cancellation and refund terms for AI Shorthand memberships.",
    sections: [
      { h: "1. Memberships and pricing", body: [
        { ul: [
          "Student Annual — £27 per year. Access to the student command library.",
          "Pro Annual — £47 per year. Full business command library.",
          "Lifetime — £99 one-time payment. Permanent Pro-level access, including future standard command and category additions.",
        ]},
        "All prices are in GBP and include VAT where applicable. Current pricing is always shown on the Pricing page before you pay.",
      ]},
      { h: "2. Free trial (Student and Pro)", body: [
        { ul: [
          "Student and Pro memberships start with a 1-day (24-hour) free trial.",
          "A valid payment card is required to start the trial. Nothing is charged at signup.",
          "Unless you cancel before the 24-hour trial ends, your card is automatically charged the annual membership fee.",
          "One free trial per customer. Lifetime membership is a direct one-time purchase with no trial.",
        ]},
      ]},
      { h: "3. Renewal and cancellation", body: [
        { ul: [
          "Student and Pro memberships renew automatically every 12 months at the then-current price until cancelled.",
          "You can cancel renewal at any time from My Account — your access continues until the end of the period you have paid for.",
          "We send a renewal reminder email before your renewal date.",
          "Lifetime membership never renews and never expires.",
        ]},
      ]},
      { h: "4. Cancellation, cooling-off and refunds", body: [
        "Your 24-hour free trial is your cancellation window. Because membership gives you immediate access to digital content, when you sign up you expressly request immediate performance and acknowledge that once your trial ends and payment is taken, the statutory 14-day cooling-off right no longer applies. This means:",
        { ul: [
          "During the 24-hour free trial you can cancel at any time from My Account at no cost — you will not be charged.",
          "If you do not cancel before the 24-hour trial ends, the annual membership payment is taken automatically.",
          "Annual payments cover the full 12-month membership period and are non-refundable once taken. There is no mid-term cancellation refund and no 14-day cooling-off after payment.",
          "You can switch off the automatic renewal at any time from My Account so you are not charged again the following year — your access continues until the end of the year you have paid for.",
          "Lifetime membership gives immediate permanent access; by purchasing you expressly consent to immediate performance and acknowledge the payment is non-refundable once access is granted.",
        ]},
        "Nothing in these terms affects your statutory rights where the service is faulty or not as described.",
        `Questions: ${COMPANY.email} or ${COMPANY.phone}.`,
      ]},
      { h: "5. Vouchers and discounts", body: [
        { ul: [
          "Discount codes apply to the first payment only unless stated otherwise.",
          "One voucher per purchase. Vouchers cannot be exchanged for cash and may be withdrawn or have expiry dates and usage limits.",
          "Retention offers presented when cancelling apply to your next payment and can be used once per customer.",
        ]},
      ]},
      { h: "6. Upgrades", body: [
        "Students can upgrade to Pro or Lifetime, and Pro members can upgrade to Lifetime, at any time from the Pricing page. Upgrades take effect immediately.",
      ]},
      { h: "7. Failed payments and price changes", body: [
        { ul: [
          "If a renewal payment fails we will retry and notify you. If payment cannot be collected, your membership access may be suspended until payment is made.",
          "We may change membership prices for future renewal periods. We will give you at least 30 days' notice by email before a price change affects you, so you can cancel if you prefer.",
        ]},
      ]},
    ],
  },

  "ai-disclaimer": {
    title: "AI Disclaimer & Acceptable Use",
    description: "Important information about AI-generated content, your responsibility for outcomes, and the acceptable use rules for AI Shorthand commands.",
    sections: [
      { h: "1. What our commands are", body: [
        TRUST,
        "Our dictionary teaches you what to ask AI. The results themselves are produced by the third-party AI tool you choose to use.",
      ]},
      { h: "2. No guarantee of AI outputs", body: [
        "AI tools can produce content that is inaccurate, incomplete, out of date, biased or inappropriate. The same command can produce different results at different times. We do not guarantee any particular outcome, quality or accuracy of content generated by third-party AI services.",
      ]},
      { h: "3. No responsibility for outcomes or results", body: [
        "We are not responsible for the outcomes you get from using our short-command prompts. Our shortcuts are starting points that help you instruct third-party AI tools — we do not control, generate, review or approve what those tools produce, or what you do with it.",
        "We make no promise of any real-world result, including but not limited to: business growth, sales, savings, income, investment returns, successful job applications or promotions, refunds or complaint outcomes, website performance, or publicity. Any examples shown are illustrations, not guarantees.",
      ]},
      { h: "4. Always review before use", body: [
        "You are responsible for reviewing, fact-checking and editing AI-generated content before relying on it or publishing it — especially names, numbers, dates, prices, legal wording and claims about people or businesses.",
      ]},
      { h: "5. Not professional advice", body: [
        "AI-generated content and our shortcut examples — including those relating to money, wealth, debt, complaints, disputes, careers, employment or wellbeing — are for general information only and are not financial, legal, tax, medical, employment or other professional advice. For regulated or important matters, always consult an appropriately qualified professional before acting.",
      ]},
      { h: "6. Your responsibility", body: [
        "You are responsible for how you use our shortcuts and any AI outputs, including compliance with applicable laws, advertising standards, confidentiality obligations, and the terms of service of the AI provider you use (such as OpenAI, Anthropic or Google).",
      ]},
      { h: "7. Acceptable use", body: [
        "You must not use AI Shorthand, our shortcuts or your membership to create, request, promote or distribute content that is:",
        { ul: [
          "Abusive, profane, threatening or harassing towards any person or group;",
          "Hateful or discriminatory — including on the grounds of race, ethnicity, nationality, religion or belief, sex, gender identity, sexual orientation, disability or age;",
          "Illegal, or that facilitates illegal activity, fraud or deception (including fake reviews, impersonation or misleading claims);",
          "Sexually explicit, or in any way sexualising minors;",
          "Promoting violence, self-harm or dangerous activity;",
          "Infringing the intellectual property, privacy or other rights of any person;",
          "Spam, malware or content intended to manipulate or deceive platforms or people.",
        ]},
        "We use automated filtering to reject inappropriate language in content submitted to our platform (such as account names, searches and partner applications). We may suspend or terminate, without refund, any membership used in breach of this policy, and where required we may report unlawful activity to the relevant authorities.",
      ]},
      { h: "8. Liability", body: [
        `To the fullest extent permitted by law, ${COMPANY.name} accepts no liability for losses arising from content generated by third-party AI services, from reliance on AI outputs, or from the outcomes of using our shortcuts. Our total liability is limited as set out in our Terms of Service. Nothing in this page affects your statutory rights as a consumer.`,
      ]},
    ],
  },

  cookies: {
    title: "Cookie Policy",
    description: "The cookies and local storage AI Shorthand uses, and how to accept or decline them.",
    sections: [
      { h: "1. What cookies are", body: [
        "Cookies are small text files stored on your device by websites you visit. We also use browser local storage — a similar technology — for simple preferences.",
      ]},
      { h: "2. What we use", body: [
        "Strictly necessary (always on) — required for the service to work:",
        { ul: [
          "Secure session cookies (access_token, refresh_token) — keep you logged in safely. Deleted or expired when you log out.",
          "cookie_consent (local storage) — remembers your cookie choice for 12 months.",
        ]},
        "Functional preferences (optional):",
        { ul: [
          "ai_output_language (local storage) — remembers your chosen AI reply language.",
          "aish_tour_done (local storage) — remembers that you have seen the welcome tour.",
        ]},
        "We do not use any advertising, analytics or cross-site tracking cookies, and we do not share cookie data with advertisers.",
      ]},
      { h: "3. Your choices", body: [
        { ul: [
          "Use the Accept or Decline buttons on our cookie banner. Declining turns off optional preference storage — the site still works, but preferences won't be remembered between visits.",
          "You can change your mind at any time using \"Cookie Settings\" in the footer.",
          "You can also block or delete cookies in your browser settings, though blocking essential cookies will prevent you from staying logged in.",
        ]},
      ]},
      { h: "4. Changes and contact", body: [
        `We will update this policy if the cookies we use change. Questions: ${COMPANY.email} or ${COMPANY.phone}.`,
      ]},
    ],
  },
};

export function LegalPage({ slug }) {
  const page = LEGAL[slug];
  useSeo({ title: page.title, description: page.description });
  return (
    <div className="max-w-3xl mx-auto px-4 py-16" data-testid={`legal-page-${slug}`}>
      <h1 className="font-display text-3xl font-bold tracking-tight">{page.title}</h1>
      <p className="text-sm text-slate-400 mt-2">Last updated: {COMPANY.updated}</p>
      <div className="mt-8 space-y-8">
        {page.sections.map((s) => (
          <section key={s.h}>
            <h2 className="font-display font-bold text-lg text-slate-900">{s.h}</h2>
            <div className="mt-2 space-y-3">
              {s.body.map((b, i) =>
                typeof b === "string"
                  ? <p key={i} className="text-[15px] text-slate-600 leading-relaxed">{b}</p>
                  : <ul key={i} className="list-disc pl-5 space-y-1.5">
                      {b.ul.map((li, j) => <li key={j} className="text-[15px] text-slate-600 leading-relaxed">{li}</li>)}
                    </ul>
              )}
            </div>
          </section>
        ))}
      </div>
      <div className="mt-12 bg-slate-50 border border-slate-200 rounded-2xl p-5 text-sm text-slate-600" data-testid="legal-company-block">
        <p className="font-bold text-slate-900">{COMPANY.name}</p>
        <p className="mt-1">Telephone: {COMPANY.phone}</p>
        <p>Email: <a href={`mailto:${COMPANY.email}`} className="text-emerald-700 hover:underline">{COMPANY.email}</a></p>
      </div>
    </div>
  );
}
